/**
 * ShiftTypeDetailPage - Detailansicht/Neuanlage eines Schichttyps (PR-4)
 * 
 * Sektionen:
 * - Grunddaten (Name)
 * - Gefahrenzone (Deaktivieren)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Clock, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface ShiftType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface FormData {
  name: string;
}

const INITIAL_FORM: FormData = {
  name: '',
};

// Generate internal code from name (not shown in UI)
const generateCodeFromName = (name: string, existingCodes: string[]): string => {
  let base = name
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  
  if (!base) base = 'TYPE';
  
  if (!existingCodes.includes(base)) return base;
  
  let suffix = 2;
  while (existingCodes.includes(`${base}_${suffix}`)) {
    suffix++;
  }
  return `${base}_${suffix}`;
};

export default function ShiftTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [shiftType, setShiftType] = useState<ShiftType | null>(null);
  const [allTypes, setAllTypes] = useState<ShiftType[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [inactiveConflict, setInactiveConflict] = useState<{ id: string; name: string } | null>(null);

  const loadAllTypes = async () => {
    try {
      const res = await fetch('/api/admin/shift-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllTypes(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichttypen:', err);
    }
  };

  const loadShiftType = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/shift-types/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setShiftType(data);
        setFormData({
          name: data.name,
        });
      } else if (res.status === 404) {
        navigate('/admin/shift-types');
      }
    } catch (err) {
      console.error('Fehler beim Laden des Schichttyps:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAllTypes();
    if (!isNew) {
      loadShiftType();
    }
  }, [id, isNew]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/shift-types' : `/api/admin/shift-types/${id}`;
      let body: { code?: string; name: string };
      
      if (isNew) {
        const existingCodes = allTypes.map(t => t.code);
        body = { 
          code: generateCodeFromName(formData.name.trim(), existingCodes), 
          name: formData.name.trim() 
        };
      } else {
        body = { name: formData.name.trim() };
      }
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        const savedId = isNew ? data.id : id;
        // Direkt zur Schichten-Übersicht mit Focus auf Typen-Sektion
        navigate(`/admin/shifts?focus=types&id=${savedId}`, { replace: true });
      } else {
        const data = await res.json().catch(() => ({}));
        // Spezielle Behandlung für Duplikate
        if (data.code === 'SHIFT_TYPE_EXISTS_ACTIVE') {
          setError('Ein Schichttyp mit diesem Namen existiert bereits');
          setInactiveConflict(null);
        } else if (data.code === 'SHIFT_TYPE_EXISTS_INACTIVE' && data.existingId) {
          setError(null);
          setInactiveConflict({ id: data.existingId, name: data.existingName || formData.name });
        } else {
          setError(data.error || data.message || 'Fehler beim Speichern');
          setInactiveConflict(null);
        }
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  const handleToggleActive = async () => {
    if (!id || isNew || !shiftType) return;
    
    setDeleting(true);
    try {
      const newIsActive = !shiftType.isActive;
      const res = await fetch(`/api/admin/shift-types/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: newIsActive }),
      });
      
      if (res.ok) {
        // UI aktualisieren
        setShiftType({ ...shiftType, isActive: newIsActive });
        setDeleteConfirmOpen(false);
      }
    } catch (err) {
      console.error('Fehler beim Statuswechsel:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !shiftType) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Schichttyp nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to="/admin/shifts" 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zu Schichten
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-6 h-6" />
              {isNew ? 'Neuer Schichttyp' : shiftType?.name}
            </h1>
            {!isNew && shiftType && !shiftType.isActive && (
              <p className="text-sm text-red-500 mt-1">(inaktiv)</p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadShiftType()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Inaktiver Konflikt - Reaktivieren anbieten */}
        {inactiveConflict && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-amber-800 mb-3">
              Ein inaktiver Schichttyp <strong>"{inactiveConflict.name}"</strong> existiert bereits.
            </p>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`/api/admin/shift-types/${inactiveConflict.id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ isActive: true }),
                  });
                  if (res.ok) {
                    const data = await res.json();
                    navigate(`/admin/shifts?focus=types&id=${data.id}`, { replace: true });
                  }
                } catch (err) {
                  console.error('Fehler beim Reaktivieren:', err);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Schichttyp reaktivieren
            </button>
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Nachtschicht"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </section>

          {/* Hinweise */}
          <section className="bg-slate-50 rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-700">Hinweise</h2>
            <div className="text-sm text-slate-600 space-y-2">
              <p>
                Schichttypen kategorisieren Schichten (z.B. Frühschicht, Spätschicht, Nachtschicht).
              </p>
              <p>
                Schichtregeln referenzieren diesen Typ, um zu definieren, wann welche Schicht aktiv ist.
              </p>
            </div>
          </section>
        </div>

        {/* Status Section (nur bei Edit) */}
        {!isNew && shiftType && shiftType.isActive && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren eines Schichttyps entfernt ihn aus der Auswahl für neue Regeln.
              Bestehende Schichtregeln und historische Daten bleiben erhalten.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Schichttyp deaktivieren
            </button>
          </section>
        )}

        {/* Reaktivieren Section (nur bei Edit + inaktiv) */}
        {!isNew && shiftType && !shiftType.isActive && (
          <section className="bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-700 mb-2">Schichttyp reaktivieren</h2>
            <p className="text-sm text-slate-600 mb-4">
              Dieser Schichttyp ist derzeit inaktiv und kann nicht für neue Schichtregeln verwendet werden.
              Durch Reaktivieren wird er wieder in der Auswahl verfügbar.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Schichttyp reaktivieren
            </button>
          </section>
        )}
      </div>

      {/* Toggle Active Confirm Modal */}
      {deleteConfirmOpen && shiftType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              {shiftType.isActive ? (
                <>
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <h2 className="text-lg font-semibold text-red-600">Schichttyp deaktivieren?</h2>
                </>
              ) : (
                <>
                  <RefreshCw className="w-6 h-6 text-blue-600" />
                  <h2 className="text-lg font-semibold text-blue-600">Schichttyp reaktivieren?</h2>
                </>
              )}
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{shiftType.name}"</strong> wird {shiftType.isActive ? 'deaktiviert' : 'reaktiviert'}.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {shiftType.isActive
                  ? 'Der Typ kann nicht mehr für neue Schichtregeln verwendet werden.'
                  : 'Der Typ kann wieder für neue Schichtregeln verwendet werden.'}
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleToggleActive}
                disabled={deleting}
                className={shiftType.isActive
                  ? "px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  : "px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"}
              >
                {deleting
                  ? (shiftType.isActive ? 'Deaktivieren...' : 'Reaktivieren...')
                  : (shiftType.isActive ? 'Deaktivieren' : 'Reaktivieren')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
